"use client";

import { useRouter } from "next/navigation";
import {
  ChevronsUpDown,
  CircleCheck,
  CirclePlus,
  LoaderCircle,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useState } from "react";
import OrganizationCreateDialog from "./organization-create-dialog";
import { useUserStore } from "@/stores/user";
import { useOrganizationStore } from "@/stores/organization";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { toast } from "sonner";
import Image from "next/image";

export function NavOrganization() {
  const router = useRouter();
  const { isMobile, setOpenMobile } = useSidebar();
  const { organizations, isLoading: isUserLoading } = useUserStore();
  const { organization, fetchOrganization } = useOrganizationStore();
  const [dialogOpen, setDialogOpen] = useState(false);

  const createOrganization = () => {
    // @TODO: Disabled until we allow more than one organization
    toast.warning("Uh, oh! This is not yet supported", {
      description:
        "For the moment, you can only create one organization. We're working on it!",
    });
    // setDialogOpen(true);
  };

  const handleOrganizationChange = async (orgId: string) => {
    if (organization && organization.id !== orgId) {
      try {
        await fetchOrganization(orgId, true);
      } catch (error: any) {
        console.warn(error.message);
        return;
      }
    }
    if (organization && organization.id === orgId) {
      router.push("/settings/general");
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg" className="max-h-8 md:p-0">
              <Avatar className="size-8 rounded-lg bg-background p-0.5">
                <AvatarImage
                  src={organization?.logo}
                  alt={organization?.name || ""}
                ></AvatarImage>
                <AvatarFallback className="rounded-lg">
                  <LoaderCircle className="flex size-4 flex-grow animate-spin self-center text-brand-primary" />
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-7">
                <span className="truncate font-display font-semibold">
                  {organization?.name || ""}
                </span>
                <span className="truncate font-display text-xs">
                  Organization
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={0}
            collisionPadding={8}
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel>Your organizations</DropdownMenuLabel>
              <TooltipProvider
                delayDuration={200}
                disableHoverableContent={true}
              >
                {isUserLoading ? (
                  <LoaderCircle className="mx-auto my-4 flex size-4 animate-spin text-brand-primary" />
                ) : (
                  organizations.map((org) => (
                    <DropdownMenuItem
                      className="text-secondary"
                      key={org.id}
                      onClick={() => {
                        if (isMobile) setOpenMobile(false);
                        handleOrganizationChange(org.id);
                      }}
                    >
                      <Image
                        src={org?.logo}
                        alt={org?.name || ""}
                        width={28}
                        height={28}
                        priority
                      />
                      <div className="grid flex-1 text-left leading-7">
                        <span className="flex items-center truncate font-display font-semibold">
                          {org?.name || ""}
                          {org.id === organization?.id && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <CircleCheck
                                  className="ml-1 text-brand-accent"
                                  size={16}
                                />
                              </TooltipTrigger>
                              <TooltipContent side="top" align="center">
                                Selected
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </TooltipProvider>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={createOrganization}>
              <CirclePlus />
              <span className="font-display">Create Organization</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      <OrganizationCreateDialog
        controlledOpen={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={async (orgId: string) => {
          await fetchOrganization(orgId, true);
          if (isMobile) setOpenMobile(false);
        }}
      />
    </SidebarMenu>
  );
}
